/**
 * App-side wiring for `@heidi/commerce/data` (D2, ARCFE-972).
 *
 * Packages are variant-agnostic and store-free (G1/F5): this module injects
 * the app's shared QueryClient so commerce's module-scope cache commands hit
 * the SAME cache the app renders from, and the REACTIVE auth token (a hook,
 * so gated queries re-key on refresh; sourced from the already-store-coupled
 * `useIsAuthenticated` module per the Rematch containment ratchet). Transport is NOT injected here —
 * `data/api` reads the `@heidi/api/client` registry, which `scribeHTTP.ts`
 * populates at factory time (E5, ARCFE-805); the side-effect import below
 * guarantees that ordering. Imported at module scope in `_app.tsx` after the
 * workspace wiring, because commerce's hooks read workspace contracts.
 *
 * Ruling 65 adds the tier, billing and trial reads to it, mirroring
 * `useAuthToken`: an ACTION crosses as the app delegate itself, reactions
 * included; a VALUE crosses as a resolved value or a hook returning one, and
 * each member below names the app expression it mirrors.
 */
// Side-effect: builds the scribe clients AND sets the @heidi/api registry.
import '@/utils/scribeHTTP';

import {
  clinicianUpgradeModalOpenAtom,
  upgradeProModalOpenAtom,
} from '@/atoms/paywallAtoms';
import { IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED } from '@/components/constants/flags';
import { configureHeidiWorkIdentityReads } from '@/features/flags/heidi-work-umbrella';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useAuthToken } from '@/hooks/useIsAuthenticated';
import useReverseTrial from '@/hooks/useReverseTrial';
import { calculateTrialDaysLeft, calculateTrialHoursLeft } from '@/lib/utils';
import { isTauri, tauriStripeRedirectUrl } from '@/tauri/utils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { configureCommerceData } from '@heidi/commerce/data';
import { getDefaultStore } from 'jotai';

configureCommerceData({
  resolveStripeRedirectBase: () =>
    isTauri() ? tauriStripeRedirectUrl() : process.env.NEXT_PUBLIC_STRIPE_REDIRECT_URL!,
  useAuthToken,
  queryClient: reactQueryClient,
  // Trial countdown math — stays app-side because it bottoms out in
  // `parseTimeStamp`, which reads the account timezone (D3, ARCFE-977).
  trialCalculators: { calculateTrialDaysLeft, calculateTrialHoursLeft },
  // Ruling 65. All three cross by reference, never wrapped: a wrapper would be
  // a second binding to keep in step, and it would drop `useReverseTrial`'s
  // memoised identity. The three copy strings are absent from the seam's type.
  useUserTier: useQueryUserTier,
  useUserBillingInfo: useQueryUserBillingInfo,
  useReverseTrial,
  // Quota-exceeded upgrade opener inputs: the atom setters bound to
  // the jotai default store — the same store `getDefaultStore()` supplies at
  // the old call site — and the flag kept a THUNK read at call time.
  openClinicianUpgradeModal: () =>
    getDefaultStore().set(clinicianUpgradeModalOpenAtom, true),
  openUpgradeProModal: () => getDefaultStore().set(upgradeProModalOpenAtom, true),
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled: () =>
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() === true,
});

// Captured when this module loads (`_app` boot). Persisted billing rehydrates
// as `success` with last session's `heidi_version`; a hide on that snapshot
// would pull Work off a user who has since moved to Heidi 2, until refetch.
const HEIDI_VERSION_READ_STARTED_AT = Date.now();

// The Work umbrella's Heidi 1 hide, read through the app billing delegate (dev
// override, enabled composition, error toast). Bound here because
// `src/features/flags` cannot import a domain `/data` door. Selector keeps the
// ~60 Work gates on the version primitive. Stale persisted data counts as
// unknown — the umbrella does not hide Work until this load's response lands.
const useHeidiVersion = () => {
  const { data: heidiVersion, dataUpdatedAt } = useQueryUserBillingInfo(
    (billing) => billing.heidi_version
  );
  if (dataUpdatedAt < HEIDI_VERSION_READ_STARTED_AT) return undefined;
  return heidiVersion;
};

configureHeidiWorkIdentityReads({ useHeidiVersion });
